import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
} from 'react'
import { getApiTargetInfo } from 'core'

import { connect } from 'react-redux'
import CommonScreen from 'admin/component/CommonScreen'

import {
  getSolarInverterGroup,
  updateSolarInverterGroup,
  deleteSolarInverterGroup,
  getSolarEntity,
  getSolarPlant,
  getSolarInverterManufacturer,
  getSolarScadaManufacturer,
  getSolarDCinputType,
  getSolarPanelTrackingType,
  getSolarOwnershipStatus,
  renameSolarInverterGroup,
  getSystemConfigurationInfo,
} from 'api/pgads'

import { ErrorContext } from 'context'
import R from 'ramda'

import { toSelectionColProps } from '@integec/grid-tools'
import { SolarInverterGroupHeaders } from 'solar/Headers'
import { Button, DatePicker, Input, InputNumber, Select, Tooltip } from 'antd'
import { HistoryModal, RenameModal } from './SolarStorageGroup'

const title = 'Solar Inverter Group'

const RANGE_CONFIG = {
  panelTiltAngle: {
    lowKey: 'SOLAR_PANEL_TILT_ANGLE_LOW',
    highKey: 'SOLAR_PANEL_TILT_ANGLE_HIGH',
    default: { min: 5, max: 45 },
  },
  minOpTemperature: {
    lowKey: 'SOLAR_MIN_OP_TEMPERATURE_LOW',
    highKey: 'SOLAR_MIN_OP_TEMPERATURE_HIGH',
    default: { min: -45, max: -10 },
  },
  maxOpTemperature: {
    lowKey: 'SOLAR_MAX_OP_TEMPERATURE_LOW',
    highKey: 'SOLAR_MAX_OP_TEMPERATURE_HIGH',
    default: { min: 40, max: 60 },
  },
}

const DEFAULT_RANGES = R.map(c => c.default, RANGE_CONFIG)

const toNum = (value, fallback) => {
  const n = parseInt(value, 10)
  return Number.isNaN(n) ? fallback : n
}

const resolveRanges = configMap =>
  R.map(
    c => ({
      min: toNum(configMap[c.lowKey], c.default.min),
      max: toNum(configMap[c.highKey], c.default.max),
    }),
    RANGE_CONFIG
  )

const getMap = (data, key, value) => {
  return R.compose(
    R.fromPairs,
    R.map(d => [d[key], d[value]])
  )(data)
}

const SolarInverterGroup = ({ apiTargetInfo }) => {
  const [insertEvent, setInsertEvent] = useState(false)
  const [entityMap, setEntityMap] = useState({})
  const [plantMap, setPlantMap] = useState({})
  const [invManufacturerMap, setInvManufacturerMap] = useState({})
  const [scadaManufacturerMap, setScadafacturerMap] = useState({})
  const [dcInputTypeMap, setDCInputTypeMap] = useState({})
  const [panelTrackingTypeMap, setPanelTrackingTypeMap] = useState({})
  const [availableStatus, setAvailableStatus] = useState({})
  const [showRenameModal, setShowRenameModal] = useState(false)
  const [showHistoryModal, setShowHistoryModal] = useState(false)
  const [ranges, setRanges] = useState(DEFAULT_RANGES)

  const { setError } = useContext(ErrorContext)

  useEffect(
    _ => {
      getSolarEntity(apiTargetInfo).fork(setError, data => {
        const cd = R.compose(
          R.fromPairs,
          R.map(d => [d.entityId, d.entityId + ' - ' + d.entityName])
        )(data)
        // setEntityMap(getMap(data, 'entityId', 'entityName'))
        setEntityMap(cd)
      })
      getSolarPlant(apiTargetInfo).fork(setError, data => {
        setPlantMap(getMap(data, 'plantId', 'plantName'))
      })
      getSolarInverterManufacturer(apiTargetInfo).fork(setError, data => {
        setInvManufacturerMap(
          getMap(data, 'manufacturerCode', 'manufacturerName')
        )
      })
      getSolarScadaManufacturer(apiTargetInfo).fork(setError, data => {
        setScadafacturerMap(
          getMap(data, 'manufacturerCode', 'manufacturerName')
        )
      })
      getSolarDCinputType(apiTargetInfo).fork(setError, data => {
        setDCInputTypeMap(getMap(data, 'dcInputTypeCode', 'dcInputTypeName'))
      })
      getSolarPanelTrackingType(apiTargetInfo).fork(setError, data => {
        setPanelTrackingTypeMap(
          getMap(data, 'trackingTypeCode', 'trackingTypeName')
        )
      })
      getSolarOwnershipStatus(apiTargetInfo).fork(setError, data => {
        setAvailableStatus(
          getMap(data, 'ownershipStatusCode', 'ownershipStatusName')
        )
      })
      getSystemConfigurationInfo(apiTargetInfo).fork(setError, data => {
        setRanges(resolveRanges(getMap(data, 'key', 'value')))
      })
    },
    [apiTargetInfo, setError]
  )

  const headerWithformater = useMemo(
    _ => {
      return SolarInverterGroupHeaders.map(header => {
        if (header.ident === 'entityId')
          return {
            ...header,
            dataFormatter: ({ value }) => entityMap[value] || value,
            sortIndexGetter: ({ rowData }) =>
              entityMap[rowData.entityId] || rowData.entityId,
          }

        if (header.ident === 'plantId')
          return {
            ...header,
            dataFormatter: ({ value }) =>
              plantMap[value] ? value + '-' + plantMap[value] : value,
            sortIndexGetter: ({ rowData }) =>
              plantMap[rowData.plantId] || rowData.plantId,
            width: 250,
          }
        if (header.ident === 'invManufacturer')
          return {
            ...header,
            ...toSelectionColProps(invManufacturerMap),
            dataFormatter: ({ value }) =>
              invManufacturerMap[value]
                ? value + '-' + invManufacturerMap[value]
                : value,
            sortIndexGetter: ({ rowData }) =>
              invManufacturerMap[rowData.invManufacturer] ||
              rowData.invManufacturer,
          }
        if (header.ident === 'scadaManufacturer')
          return {
            ...header,
            ...toSelectionColProps(scadaManufacturerMap),
            dataFormatter: ({ value }) =>
              scadaManufacturerMap[value]
                ? value + '-' + scadaManufacturerMap[value]
                : value,
            sortIndexGetter: ({ rowData }) =>
              scadaManufacturerMap[rowData.scadaManufacturer] ||
              rowData.scadaManufacturer,
          }

        if (header.ident === 'dcInputType')
          return {
            ...header,
            ...toSelectionColProps(dcInputTypeMap),
            dataFormatter: ({ value }) =>
              dcInputTypeMap[value]
                ? value + '-' + dcInputTypeMap[value]
                : value,
            sortIndexGetter: ({ rowData }) =>
              dcInputTypeMap[rowData.dcInputType] || rowData.dcInputType,
          }

        if (header.ident === 'panelTrackingType')
          return {
            ...header,
            ...toSelectionColProps(panelTrackingTypeMap),
            dataFormatter: ({ value }) =>
              panelTrackingTypeMap[value]
                ? value + '-' + panelTrackingTypeMap[value]
                : value,
            sortIndexGetter: ({ rowData }) =>
              panelTrackingTypeMap[rowData.panelTrackingType] ||
              rowData.panelTrackingType,
          }

        if (header.ident === 'invOwnershipStatus')
          return {
            ...header,
            ...toSelectionColProps(availableStatus),
            dataFormatter: ({ value }) =>
              availableStatus[value]
                ? value + '-' + availableStatus[value]
                : value,
            sortIndexGetter: ({ rowData }) =>
              availableStatus[rowData.invOwnershipStatus] ||
              rowData.invOwnershipStatus,
          }

        return header
      })
    },
    [
      entityMap,
      plantMap,
      invManufacturerMap,
      scadaManufacturerMap,
      dcInputTypeMap,
      panelTrackingTypeMap,
      availableStatus,
    ]
  )

  const InsertComp = useCallback(
    comProps => (
      <SolarInverterGroupUpdate
        {...comProps}
        entityMap={entityMap}
        plantMap={plantMap}
        invManufacturerMap={invManufacturerMap}
        scadaManufacturerMap={scadaManufacturerMap}
        dcInputTypeMap={dcInputTypeMap}
        panelTrackingTypeMap={panelTrackingTypeMap}
        availableStatus={availableStatus}
        ranges={ranges}
      />
    ),
    [
      entityMap,
      plantMap,
      invManufacturerMap,
      scadaManufacturerMap,
      dcInputTypeMap,
      panelTrackingTypeMap,
      availableStatus,
      ranges,
    ]
  )

  const extraCriteriaAfterButton = ({
    data,
    onRetrieve,
    apiTargetInfo,
    setSpinning,
  }) => {
    return (
      <div>
        {showRenameModal && (
          <RenameModal
            list={data.map(e => ({
              label: e.inverterGroupId,
              value: e.inverterGroupId + '-' + e.plantId,
            }))}
            modalFor="Inverter Group"
            hide={() => setShowRenameModal(false)}
            onOk={data => {
              const { oldId, newId } = data
              const ids = oldId?.split('-')

              setSpinning(true)
              renameSolarInverterGroup(apiTargetInfo)({
                oldId: ids[0],
                plantId: ids[1],
                newId,
              }).fork(setError, _ => {
                setSpinning(false)
                onRetrieve()
              })
            }}
          />
        )}
        {showHistoryModal && (
          <HistoryModal
            dataName="INVERTER GROUP"
            apiTargetInfo={apiTargetInfo}
            setError={setError}
            hide={() => setShowHistoryModal(false)}
          />
        )}
        <Button
          onClick={() => setShowRenameModal(true)}
          type="primary"
          // style={{ marginLeft: '10px' }}
        >
          Rename Inverter Group
        </Button>
        <Button
          onClick={() => setShowHistoryModal(true)}
          type="primary"
          style={{ marginLeft: '10px' }}
        >
          Show Rename History
        </Button>
      </div>
    )
  }

  return (
    <CommonScreen
      title={title}
      header={headerWithformater}
      saveTask={updateSolarInverterGroup}
      retrieveTask={getSolarInverterGroup}
      deleteTask={deleteSolarInverterGroup}
      altBy={d => d.entityId}
      autoRetrieve
      showDatePickerOnEdit
      //   sortOptions={[
      //     strCol({
      //       ident: 'plantName',
      //       display: 'Plant Name',
      //       isKey: true,
      //       sortOrder: 'asc',
      //     }),
      //   ]}
      //   initialSortOptions={[
      //     strCol({
      //       ident: 'plantName',
      //       display: 'Plant Name',
      //       isKey: true,
      //       sortOrder: 'asc',
      //     }),
      //   ]}
      widthOffset={50}
      extraCriteriaAfterButton={extraCriteriaAfterButton}
      insertComp={insertEvent && InsertComp}
      insertCompHeight={200}
      onInsertEvent={b => setInsertEvent(b)}
      autoFixColByKey={false}
      headerRowHeight={60}
      defaultInsertData={{ daylightSaving: 'N', plantOwnershipStatus: 'AV' }}
    />
  )
}

const mapStateToProps = state => ({
  apiTargetInfo: getApiTargetInfo(state),
})

export default connect(mapStateToProps)(SolarInverterGroup)

const SolarInverterGroupUpdate = ({
  setInsertData,
  insertData,
  onAdd,
  onCancel,
  entityMap = {},
  plantMap = {},
  invManufacturerMap = {},
  scadaManufacturerMap = {},
  dcInputTypeMap = {},
  panelTrackingTypeMap = {},
  availableStatus = {},
  ranges = DEFAULT_RANGES,
}) => {
  const getLabel = useCallback((key, isKey, isRequired) => {
    return (
      <div>
        {isKey && <span style={{ color: 'red', marginRight: '2px' }}>*</span>}
        {!isKey && isRequired && (
          <span style={{ color: 'red', marginRight: '2px' }}>*</span>
        )}
        {(SolarInverterGroupHeaders.find(f => f.ident === key) &&
          SolarInverterGroupHeaders.find(f => f.ident === key).display) ||
          ''}{' '}
        :
      </div>
    )
  }, [])

  const getInput = useCallback(
    ({
      key,
      maxLength,
      isKey,
      insertData,
      setInsertData,
      isRequired = false,
    }) => (
      <div>
        <span style={{ display: 'flex' }}>
          {getLabel(key, isKey, isRequired)}
        </span>
        <Input
          disabled={insertData.insert && isKey}
          className={key}
          data-cy={key}
          value={insertData[key]}
          onChange={e =>
            setInsertData({ ...insertData, [key]: e.target.value })
          }
          maxLength={maxLength}
        />
      </div>
    ),
    [getLabel]
  )
  const getMapSelect = useCallback(
    ({
      key,
      keymap,
      isKey,
      setInsertData,
      insertData,
      isRequired = false,
      extStyle = {},
    }) => (
      <div style={{ ...extStyle }}>
        <span style={{ display: 'flex' }}>
          {getLabel(key, isKey, isRequired)}
        </span>
        <Select
          showSearch
          disabled={insertData.insert && isKey}
          className={key}
          data-cy={key}
          style={{ width: '100%', zIndex: 500 }}
          value={insertData[key]}
          onChange={val => setInsertData({ ...insertData, [key]: val })}
          filterOption={(input, option) =>
            option.props.children.toLowerCase().indexOf(input.toLowerCase()) >=
            0
          }
        >
          {Object.keys(keymap).map(k => (
            <Select.Option value={k} key={k} className={key} data-cy={key}>
              {/* {`${k}-${keymap[k]}`} */}
              {`${keymap[k]}`}
            </Select.Option>
          ))}
        </Select>
      </div>
    ),
    [getLabel]
  )

  const getInputNumber = useCallback(
    ({
      key,
      precision = 0,
      isKey = false,
      insertData,
      setInsertData,
      isRequired = false,
      min,
      max,
    }) => (
      <div>
        <span style={{ display: 'flex' }}>
          {getLabel(key, isKey, isRequired)}
        </span>
        <Tooltip
          placement="top"
          title={
            (min != null || max != null) &&
            (min != null ? 'Min ' + min + ', ' : ' ') +
              (max != null ? 'Max ' + max : '')
          }
        >
          <InputNumber
            disabled={insertData.insert && isKey}
            style={{ width: '100%' }}
            value={insertData[key]}
            className={key}
            data-cy={key}
            onChange={value =>
              setInsertData({ ...insertData, [key]: value + '' })
            }
            precision={precision}
            min={min}
            max={max}
          />
        </Tooltip>
      </div>
    ),
    [getLabel]
  )
  const getDate = useCallback(
    ({ key, insertData, isKey, setInsertData, isRequired = false }) => (
      <div>
        {getLabel(key, isKey, isRequired)}
        <DatePicker
          style={{ width: '100%' }}
          format="MM/DD/YYYY"
          className={key}
          data-cy={key}
          value={insertData[key + 'date']}
          onChange={d => {
            setInsertData({
              ...insertData,
              [key]: d.format('YYYY-MM-DD'),
              [key + 'date']: d,
            })
          }}
        />
      </div>
    ),
    [getLabel]
  )

  return (
    <div
      style={{
        display: 'grid',
        // gridTemplateColumns: '250px 250px 280px 210px 210px 210px 210px',
        gridTemplateColumns: 'repeat(4, 285px)',
        gridTemplateRows: '60px 60px 60px',
        gridColumnGap: '10px',
        gridRowGap: '7px',
      }}
    >
      {getMapSelect({
        setInsertData,
        insertData,
        key: 'entityId',
        isKey: true,
        keymap: entityMap,
        ellipsis: true,
      })}
      {getMapSelect({
        setInsertData,
        insertData,
        key: 'plantId',
        isKey: true,
        keymap: plantMap,
        width: 150,
      })}
      {getInput({
        setInsertData,
        insertData,
        key: 'inverterGroupId',
        isKey: true,
      })}
      {getInput({
        setInsertData,
        insertData,
        key: 'inverterGroupName',
        isKey: true,
      })}
      {getDate({
        setInsertData,
        insertData,
        key: 'commissionDate',
        maxLength: 10,
        isRequired: true,
      })}
      {getDate({
        setInsertData,
        insertData,
        key: 'effectiveDate',
        maxLength: 10,
        isRequired: true,
      })}
      {getInputNumber({
        setInsertData,
        insertData,
        key: 'groupInstalledCap',
        isRequired: true,
        precision: 2,
      })}
      {getInputNumber({
        setInsertData,
        insertData,
        key: 'numInverters',
        isRequired: true,
      })}
      {getInputNumber({
        setInsertData,
        insertData,
        key: 'singleInvSystemCap',
        // max: 30,
        min: 0.01,
        isRequired: true,
        precision: 3,
      })}
      {getInputNumber({
        setInsertData,
        insertData,
        key: 'singleInvNameplateCap',
        max: 30,
        min: 0.01,
        precision: 3,
        isRequired: true,
      })}
      {getMapSelect({
        setInsertData,
        insertData,
        key: 'invOwnershipStatus',

        keymap: availableStatus,
        isRequired: true,
      })}
      {getMapSelect({
        setInsertData,
        insertData,
        key: 'invManufacturer',

        keymap: invManufacturerMap,
      })}
      {getInput({
        setInsertData,
        insertData,
        key: 'invModel',
      })}
      {getMapSelect({
        setInsertData,
        insertData,
        key: 'scadaManufacturer',

        keymap: scadaManufacturerMap,
      })}
      {getInput({
        setInsertData,
        insertData,
        key: 'scadaModel',
      })}
      {getMapSelect({
        setInsertData,
        insertData,
        key: 'dcInputType',
        keymap: dcInputTypeMap,
        isRequired: true,
        width: 200,
      })}
      {getInputNumber({
        setInsertData,
        insertData,
        key: 'dcAcFieldCapRatio',
        max: 2,
        min: 1,
        width: 200,
        precision: 3,
        isRequired: true,
      })}
      {getMapSelect({
        setInsertData,
        insertData,
        key: 'panelTrackingType',
        keymap: panelTrackingTypeMap,
        isRequired: true,
      })}
      {getInputNumber({
        setInsertData,
        insertData,
        key: 'panelTiltAngle',
        max: ranges.panelTiltAngle.max,
        min: ranges.panelTiltAngle.min,
        isRequired: true,
      })}
      {getInputNumber({
        setInsertData,
        insertData,
        key: 'minIrradiance',
        // max: 5000,
        // min: 1,
        isRequired: true,
      })}
      {getInputNumber({
        setInsertData,
        insertData,
        key: 'stowingWindSpeed',
      })}
      {getInputNumber({
        setInsertData,
        insertData,
        key: 'minOpTemperature',
        max: ranges.minOpTemperature.max,
        min: ranges.minOpTemperature.min,
        isRequired: true,
      })}
      {getInputNumber({
        setInsertData,
        insertData,
        key: 'maxOpTemperature',
        max: ranges.maxOpTemperature.max,
        min: ranges.maxOpTemperature.min,
        isRequired: true,
      })}
      {getInputNumber({
        setInsertData,
        insertData,
        key: 'temperatureCoefficient',
        precision: 3,
        isRequired: true,
      })}
      {getInputNumber({
        setInsertData,
        insertData,
        key: 'nameplateEfficiency',
        precision: 2,
        isRequired: true,
      })}
      {getInput({
        setInsertData,
        insertData,
        key: 'transferToEntityId',
        maxLength: 10,
      })}
      <div
        style={{
          gridColumnEnd: 'span 2',
          display: 'grid',
          gridTemplateColumns: 'min-content max-content min-content',
          gap: 10,
          // paddingBottom: '7px',
          alignContent: 'end',
        }}
      >
        <Button type="primary" onClick={() => onAdd(true)}>
          Add
        </Button>
        <Button type="primary" onClick={() => onAdd(false)}>
          Add & Hide
        </Button>
        <Button type="danger" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  )
}
